import React, { useMemo } from 'react';

import { ScaleRoot, SliderBubble, SliderRange } from './scale.styles';
import { SliderConfig, resolveSliderConfig } from './scaleTypes';
import ScaleLabels from './ScaleLabels';
import { ScaleBlockProps, useScaleValue } from './useScaleValue';

const SliderInput = React.forwardRef<HTMLInputElement, ScaleBlockProps<SliderConfig>>(
  (props, ref) => {
    const config = useMemo(
      () => resolveSliderConfig(props.data?.blockConfig),
      [props.data?.blockConfig],
    );
    const { value, commit, interactive, isLiveForm, showError, hiddenInput } = useScaleValue(
      props,
      ref,
    );

    const min = Number(config.min) || 0;
    const max = Math.max(min + 1, Number(config.max) || min + 1);
    const step = Math.max(0.01, Number(config.step) || 1);
    const fallback = Math.min(max, Math.max(min, Number(config.defaultValue) || min));

    const hasValue = value !== '';
    const current = hasValue ? Number(value) : fallback;
    const fill = ((current - min) / (max - min)) * 100;
    const display = `${config.prefix}${current.toLocaleString()}${config.suffix}`;

    return (
      <ScaleRoot className={`scale-block${isLiveForm ? ' is-live' : ''}${showError ? ' has-error' : ''}`}>
        <div className="slider-field">
          <SliderBubble
            $fill={fill}
            className={`slider-bubble${hasValue ? '' : ' is-placeholder'}`}
          >
            {display}
          </SliderBubble>
          <SliderRange
            type="range"
            className={`slider-input${hasValue ? '' : ' is-placeholder'}`}
            min={min}
            max={max}
            step={step}
            value={current}
            $fill={fill}
            disabled={!interactive}
            aria-valuetext={display}
            onChange={(e) => commit(e.target.value)}
          />
        </div>
        <ScaleLabels
          low={config.lowLabel || `${config.prefix}${min.toLocaleString()}${config.suffix}`}
          high={config.highLabel || `${config.prefix}${max.toLocaleString()}${config.suffix}`}
        />
        {showError && <div className="scale-error-text">This field is required.</div>}
        <input {...hiddenInput} />
      </ScaleRoot>
    );
  },
);

SliderInput.displayName = 'SliderInput';

export default SliderInput;
